import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { alpha } from '@mui/material/styles';
import { CANVAS_W, CANVAS_H, HOTSPOT_KEYFRAMES } from './constants';
import { getHotspotCentroid } from './helpers';

// ── Pulse / ring indicator shown on clickable hotspots ────────────────────────
function PulseIndicator({ cx, cy, color, hovered }) {
  const r = 10.5; // 7 × 1.5 — 50% bigger
  return (
    <g pointerEvents="none">
      {/* Expanding ring — always animating unless hovered */}
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={1.5} opacity={0}
        style={{ animation: hovered ? 'none' : 'hs-ring 2s ease-out infinite', animationDelay: '0.4s' }} />
      {/* Solid filled dot — always fully visible, white stroke for contrast */}
      <circle cx={cx} cy={cy} r={r} fill={color} stroke="#ffffff" strokeWidth={2}
        style={{ animation: hovered ? 'none' : 'hs-pulse 2s ease-in-out infinite' }} />
    </g>
  );
}

// ── Single hotspot shape (rect / circle / bezier / polygon) ───────────────────
export function HotspotShape({ hotspot, isClickable, onClick, shimmerDelay, onHover, onMove, onLeave, bpCanvasW = CANVAS_W, bpCanvasH = CANVAS_H }) {
  const [hovered, setHovered] = useState(false);

  const glowColor    = hotspot.glowColor         || '#1075BA';
  const fillColor    = hotspot.hoverFillColor    || glowColor;
  const fillOpacity  = hotspot.hoverFillOpacity  ?? 0.2;
  const bordColor    = hotspot.hoverBorderColor  || glowColor;
  const bordOpacity  = hotspot.hoverBorderOpacity ?? 0.85;
  const sw           = hotspot.strokeWidth       ?? 1.5;
  const glowOpacity  = hotspot.glowOpacity       ?? 0.5;
  const isActive     = hotspot.isActive !== false;

  const showStyle   = hovered && isClickable;
  const fill        = alpha(fillColor, showStyle ? fillOpacity : 0);
  const stroke      = alpha(bordColor, showStyle ? bordOpacity : 0);
  const glowFilter  = showStyle && glowOpacity > 0
    ? `drop-shadow(0 0 8px ${alpha(glowColor, glowOpacity)})`
    : 'none';

  const groupStyle    = { opacity: isActive ? 1 : 0.3, cursor: isClickable ? 'pointer' : 'default' };
  const shapeTransition = { transition: 'fill 0.2s ease, stroke 0.2s ease, filter 0.2s ease' };

  const pxX = p => (p / 100) * bpCanvasW;
  const pxY = p => (p / 100) * bpCanvasH;

  const hasShimmer  = shimmerDelay !== null && shimmerDelay !== undefined;
  const shimmerStyle = hasShimmer
    ? { animation: 'hs-shape-shimmer 0.8s ease-in-out forwards', animationDelay: `${shimmerDelay}ms` }
    : null;

  const handlers = {
    ...(isClickable ? { onClick: e => { e.stopPropagation(); onClick(hotspot); } } : {}),
    onMouseEnter: e => { setHovered(true);  onHover?.(hotspot.label, e); },
    onMouseMove:  e => { onMove?.(e); },
    onMouseLeave: e => { setHovered(false); onLeave?.(); },
  };

  // ── Rect ──
  if (hotspot.shape === 'rect' && hotspot.points?.[0] && hotspot.meta) {
    const x = pxX(hotspot.points[0].x), y = pxY(hotspot.points[0].y);
    const w = pxX(hotspot.meta.width || 10), h = pxY(hotspot.meta.height || 10);
    return (
      <g style={groupStyle} {...handlers}>
        <rect x={x} y={y} width={w} height={h} fill={fill} stroke={stroke} strokeWidth={sw} rx={4} style={{ ...shapeTransition, filter: glowFilter }} />
        {shimmerStyle && <rect x={x} y={y} width={w} height={h} rx={4} fill={fillColor} stroke="none" opacity={0} style={shimmerStyle} pointerEvents="none" />}
        <PulseIndicator cx={x + w / 2} cy={y + h / 2} color={bordColor} hovered={hovered && isClickable} />
      </g>
    );
  }

  // ── Circle ──
  if (hotspot.shape === 'circle' && hotspot.points?.[0]) {
    const cx = pxX(hotspot.points[0].x), cy = pxY(hotspot.points[0].y);
    const r  = pxX(hotspot.meta?.radius || 5);
    return (
      <g style={groupStyle} {...handlers}>
        <circle cx={cx} cy={cy} r={r} fill={fill} stroke={stroke} strokeWidth={sw} style={{ ...shapeTransition, filter: glowFilter }} />
        {shimmerStyle && <circle cx={cx} cy={cy} r={r} fill={fillColor} stroke="none" opacity={0} style={shimmerStyle} pointerEvents="none" />}
        <PulseIndicator cx={cx} cy={cy} color={bordColor} hovered={hovered && isClickable} />
      </g>
    );
  }

  // ── Bezier path ──
  if (hotspot.shape === 'polygon' && hotspot.meta?.isBezier && hotspot.meta?.pathD) {
    const pts = hotspot.points || [];
    const xs  = pts.map(p => pxX(p.x)), ys = pts.map(p => pxY(p.y));
    const cx  = pts.length ? (Math.min(...xs) + Math.max(...xs)) / 2 : 0;
    const cy  = pts.length ? (Math.min(...ys) + Math.max(...ys)) / 2 : 0;
    return (
      <g style={groupStyle} {...handlers}>
        <path d={hotspot.meta.pathD} fill={fill} stroke={stroke} strokeWidth={sw} style={{ ...shapeTransition, filter: glowFilter }} />
        {shimmerStyle && <path d={hotspot.meta.pathD} fill={fillColor} stroke="none" opacity={0} style={shimmerStyle} pointerEvents="none" />}
        <PulseIndicator cx={cx} cy={cy} color={bordColor} hovered={hovered && isClickable} />
      </g>
    );
  }

  // ── Polygon ──
  if (hotspot.points?.length > 1) {
    const ptsStr = hotspot.points.map(p => `${pxX(p.x)},${pxY(p.y)}`).join(' ');
    const xs     = hotspot.points.map(p => pxX(p.x)), ys = hotspot.points.map(p => pxY(p.y));
    const cx     = (Math.min(...xs) + Math.max(...xs)) / 2;
    const cy     = (Math.min(...ys) + Math.max(...ys)) / 2;
    return (
      <g style={groupStyle} {...handlers}>
        <polygon points={ptsStr} fill={fill} stroke={stroke} strokeWidth={sw} style={{ ...shapeTransition, filter: glowFilter }} />
        {shimmerStyle && <polygon points={ptsStr} fill={fillColor} stroke="none" opacity={0} style={shimmerStyle} pointerEvents="none" />}
        <PulseIndicator cx={cx} cy={cy} color={bordColor} hovered={hovered && isClickable} />
      </g>
    );
  }

  return null;
}

// ── EngTextElement ─────────────────────────────────────────────────────────────
// Renders a text overlay element in the engine (public/preview view).
// Matches the same positioning logic as TextElement in LobbyCanvas.
export function EngTextElement({ hotspot, canvasScale = 1 }) {
  const isActive = hotspot.isActive !== false;
  if (!isActive) return null;

  const x = hotspot.points?.[0]?.x ?? 10;
  const y = hotspot.points?.[0]?.y ?? 10;
  const w = hotspot.meta?.width    ?? 35;
  const h = hotspot.meta?.height   ?? 12;

  const text          = hotspot.meta?.text          ?? '';
  const fontSize      = (hotspot.meta?.fontSize      ?? 25) * canvasScale;
  const fontWeight    = hotspot.meta?.fontWeight     ?? 600;
  const color         = hotspot.meta?.color          ?? '#ffffff';
  const textAlign     = hotspot.meta?.textAlign      ?? 'center';
  const bgColor       = hotspot.meta?.bgColor        ?? 'rgba(0,0,0,0.7)';
  const bgStrokeColor = hotspot.meta?.bgStrokeColor  ?? '#ffffff';
  const bgStrokeWidth = (hotspot.meta?.bgStrokeWidth ?? 3) * canvasScale;
  const borderRadius  = (hotspot.meta?.borderRadius  ?? 6) * canvasScale;

  return (
    <>
      <style>{`
        @keyframes eng-text-reveal {
          0%   { transform: scale(0.82); opacity: 0; }
          60%  { transform: scale(1.03); opacity: 1; }
          100% { transform: scale(1);    opacity: 1; }
        }
        @keyframes eng-text-breathe {
          0%   { transform: scale(1);     }
          50%  { transform: scale(0.982); }
          100% { transform: scale(1.012); }
        }
      `}</style>
      <div
        style={{
          position: 'absolute',
          left:   `${x}%`,
          top:    `${y}%`,
          width:  `${w}%`,
          height: `${h}%`,
          zIndex: 5 + (hotspot.order ?? 0),
          background:   bgColor,
          border:       bgStrokeWidth > 0 ? `${bgStrokeWidth}px solid ${bgStrokeColor}` : 'none',
          borderRadius: `${borderRadius}px`,
          display:      'flex',
          alignItems:   'center',
          justifyContent: textAlign === 'left' ? 'flex-start' : textAlign === 'right' ? 'flex-end' : 'center',
          padding:      '0 6px',
          boxSizing:    'border-box',
          overflow:     'hidden',
          pointerEvents: 'none',
          userSelect:   'none',
          opacity:      0,
          animation:    'eng-text-reveal 0.55s cubic-bezier(0.34,1.56,0.64,1) 0.5s forwards, eng-text-breathe 4s ease-in-out 1.05s infinite alternate',
          transformOrigin: 'center center',
        }}
      >
        <div
          style={{
            width:      '100%',
            color,
            fontWeight,
            fontSize:   `${fontSize}px`,
            textAlign,
            lineHeight: 1.3,
            wordBreak:  'break-word',
            whiteSpace: 'pre-wrap',
          }}
        >
          {text}
        </div>
      </div>
    </>
  );
}

// ── Full SVG overlay layer ────────────────────────────────────────────────────
export function HotspotSVGLayer({ hotspots, isClickable, onHotspotClick, bpCanvasW = CANVAS_W, bpCanvasH = CANVAS_H }) {
  const activeHotspots = hotspots.filter(h => h.isActive !== false);

  // Build sorted list for wave shimmer
  const sortedByX = [...activeHotspots]
    .filter(h => isClickable(h))
    .map(h => ({ id: h.id, cx: getHotspotCentroid(h).cx }))
    .sort((a, b) => a.cx - b.cx);

  const [shimmerDelays, setShimmerDelays] = useState({});
  const anyHoveredRef = useRef(false);
  const sortedKey     = sortedByX.map(h => h.id).join(',');

  useEffect(() => {
    const fire = () => {
      if (anyHoveredRef.current) return;
      const delays = {};
      sortedByX.forEach((h, i) => { delays[h.id] = i * 120; });
      setShimmerDelays(delays);
      setTimeout(() => setShimmerDelays({}), sortedByX.length * 120 + 800);
    };
    const interval = setInterval(fire, 4000);
    return () => clearInterval(interval);
  }, [sortedKey]); // eslint-disable-line 

  // Tooltip
  const [tooltip, setTooltip] = useState(null);
  const tooltipTimeoutRef     = useRef(null);

  const handleShapeHover = useCallback((label, e) => {
    anyHoveredRef.current = true;
    setShimmerDelays({});
    if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
    if (label) setTooltip({ label, x: e.clientX, y: e.clientY });
  }, []);

  const handleShapeMove  = useCallback((e) => {
    if (tooltip) setTooltip(prev => prev ? { ...prev, x: e.clientX, y: e.clientY } : null);
  }, [tooltip]);

  const handleShapeLeave = useCallback(() => {
    anyHoveredRef.current = false;
    tooltipTimeoutRef.current = setTimeout(() => setTooltip(null), 80);
  }, []);

  return (
    <>
      <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 2 }}
        viewBox={`0 0 ${bpCanvasW} ${bpCanvasH}`} preserveAspectRatio="xMidYMid meet">
        <defs><style>{HOTSPOT_KEYFRAMES}</style></defs>
        {activeHotspots.map(h => (
          <HotspotShape
            key={h.id}
            hotspot={h}
            isClickable={isClickable(h)}
            onClick={onHotspotClick}
            shimmerDelay={shimmerDelays[h.id] ?? null}
            onHover={handleShapeHover}
            onMove={handleShapeMove}
            onLeave={handleShapeLeave}
            bpCanvasW={bpCanvasW}
            bpCanvasH={bpCanvasH}
          />
        ))}
      </svg>

      {tooltip && ReactDOM.createPortal(
        <div style={{ position: 'fixed', top: tooltip.y - 42, left: tooltip.x, transform: 'translateX(-50%)', pointerEvents: 'none', zIndex: 9999, background: 'rgba(10,14,26,0.88)', color: '#fff', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', padding: '5px 11px', borderRadius: 6, whiteSpace: 'nowrap', border: '1px solid rgba(255,255,255,0.12)', boxShadow: '0 4px 12px rgba(0,0,0,0.35)', letterSpacing: '0.02em' }}>
          {tooltip.label}
        </div>,
        document.body,
      )}
    </>
  );
}

// ── EngImageElement ────────────────────────────────────────────────────────────
// Renders an image overlay element in the engine. Mirrors ImageElement in LobbyCanvas.

const IMG_DOT_CSS = `
  @keyframes img-dot-pulse { 0%,100% { transform: translate(-50%,-50%) scale(1); } 50% { transform: translate(-50%,-50%) scale(1.28); } }
  @keyframes img-dot-ring  { 0% { transform: translate(-50%,-50%) scale(1); opacity: 0.7; } 100% { transform: translate(-50%,-50%) scale(2.8); opacity: 0; } }
`;

function ImageLinkDot({ color = '#1075BA' }) {
  return (
    <>
      <style>{IMG_DOT_CSS}</style>
      {/* Expanding ring */}
      <div style={{
        position: 'absolute', top: '50%', left: '50%',
        width: 21, height: 21, borderRadius: '50%',
        border: `1.5px solid ${color}`,
        boxSizing: 'border-box',
        pointerEvents: 'none',
        animation: 'img-dot-ring 2s ease-out 0.4s infinite',
      }} />
      {/* Solid dot */}
      <div style={{
        position: 'absolute', top: '50%', left: '50%',
        width: 21, height: 21, borderRadius: '50%',
        background: color,
        border: '2px solid #ffffff',
        boxSizing: 'border-box',
        boxShadow: `0 0 8px ${color}88`,
        pointerEvents: 'none',
        animation: 'img-dot-pulse 2s ease-in-out infinite',
        transform: 'translate(-50%, -50%)',
      }} />
    </>
  );
}

export function EngImageElement({ hotspot, canvasScale = 1, onHotspotClick }) {
  const isActive = hotspot.isActive !== false;
  if (!isActive) return null;

  const x = hotspot.points?.[0]?.x ?? 10;
  const y = hotspot.points?.[0]?.y ?? 10;
  const w = hotspot.meta?.width    ?? 25;
  const h = hotspot.meta?.height   ?? 14;

  const imageUrl      = hotspot.meta?.imageUrl      ?? null;
  const objectFit     = hotspot.meta?.objectFit     ?? 'cover';
  const bgStrokeColor = hotspot.meta?.bgStrokeColor ?? 'transparent';
  const bgStrokeWidth = (hotspot.meta?.bgStrokeWidth ?? 0) * canvasScale;
  const borderRadius  = (hotspot.meta?.borderRadius  ?? 0) * canvasScale;

  if (!imageUrl) return null;

  const hasLink = !!(hotspot.hotspotAction?.targetId || hotspot.hotspotAction?.externalUrl);
  const dotColor = hotspot.glowColor || '#1075BA';

  return (
    <div
      onClick={hasLink && onHotspotClick ? () => onHotspotClick(hotspot) : undefined}
      style={{
        position:   'absolute',
        left:       `${x}%`,
        top:        `${y}%`,
        width:      `${w}%`,
        height:     `${h}%`,
        zIndex:     5 + (hotspot.order ?? 0),
        border:     bgStrokeWidth > 0 ? `${bgStrokeWidth}px solid ${bgStrokeColor}` : 'none',
        borderRadius: `${borderRadius}px`,
        overflow:   'hidden',
        pointerEvents: hasLink ? 'all' : 'none',
        cursor:     hasLink ? 'pointer' : 'default',
        userSelect: 'none',
        boxSizing:  'border-box',
      }}
    >
      <img
        src={imageUrl}
        alt=""
        style={{ width: '100%', height: '100%', objectFit, display: 'block', pointerEvents: 'none' }}
      />
      {hasLink && <ImageLinkDot color={dotColor} />}
    </div>
  );
}